import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ApprovalFilters from "./ApprovalFilters";
import ApprovalGroup from "./ApprovalGroup";
import ReviewPanel from "./ReviewPanel";
import { getAllocationRequests } from "../../../../../api/approvals";
import PageInfo from "../../../../../components/common/PageInfo";

const FILTER_TO_STATUS = {
  all: undefined,
  needs_pmo: ["SUBMITTED", "MOVE_PENDING"],
  at_scg: "PMO_APPROVED",
  approved: ["ACTIVE", "ENDED"],
  rejected: "REJECTED",
};

const groupByStatus = (requests) => ({
  SUBMITTED: requests.filter((r) => r.status === "SUBMITTED" || r.status === "MOVE_PENDING"),
  PMO_APPROVED: requests.filter((r) => r.status === "PMO_APPROVED"),
  ACTIVE: requests.filter((r) => r.status === "ACTIVE"),
  ENDED: requests.filter((r) => r.status === "ENDED"),
  REJECTED: requests.filter((r) => r.status === "REJECTED"),
});

const deriveSummary = (requests) => ({
  all: requests.length,
  needsPmo: requests.filter((r) => r.status === "SUBMITTED" || r.status === "MOVE_PENDING").length,
  atScg: requests.filter((r) => r.status === "PMO_APPROVED").length,
  approved: requests.filter((r) => r.status === "ACTIVE" || r.status === "ENDED").length,
  rejected: requests.filter((r) => r.status === "REJECTED").length,
});

const Approvals = ({ onSummaryLoad, initialRequestCode, onInitialRequestHandled, defaultFilter = "needs_pmo" }) => {
  const theme = useTheme();
  const t = theme.custom;

  const [allRequests, setAllRequests] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(defaultFilter);
  const [search, setSearch] = useState("");
  const [reviewRequest, setReviewRequest] = useState(null);

  const [approvedSubFilter, setApprovedSubFilter] = useState("all"); // "all" | "active" | "ended"

  // Fetch full list once on mount — used to derive summary counts
  const fetchAllRequests = useCallback(async () => {
    try {
      const data = await getAllocationRequests();
      setAllRequests(data);
      const s = deriveSummary(data);
      onSummaryLoad?.(s.needsPmo);
    } catch {
      // Non-fatal — summary stays at zero
    }
  }, [onSummaryLoad]);

  useEffect(() => { fetchAllRequests(); }, [fetchAllRequests]);

  // Fetch filtered list whenever active filter changes
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      const status = FILTER_TO_STATUS[activeFilter];
      if (Array.isArray(status)) {
        params.status = status.join(",");
      } else if (status) {
        params.status = status;
      }
      const data = await getAllocationRequests(params);
      setRequests(data);
    } catch {
      setError("Failed to load allocation requests");
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Called after approve or reject completes — refreshes both lists
  const handleActionDone = useCallback(() => {
    setReviewRequest(null);
    fetchRequests();
    fetchAllRequests();
  }, [fetchRequests, fetchAllRequests]);

  // Auto-open review panel when navigated via deep-link from email
  useEffect(() => {
    if (!initialRequestCode || requests.length === 0) return;
    const match = requests.find((r) => r.requestCode === initialRequestCode);
    if (match) {
      setReviewRequest(match);
      onInitialRequestHandled?.();
    }
  }, [requests, initialRequestCode, onInitialRequestHandled]);

  // Client-side search on top of filtered API results
  const filteredRequests = requests.filter((r) => {
    if (!search.trim()) return true;
    const tokens = search.toLowerCase().trim().split(/\s+/);

    const positionTypeLabel =
      r.positionType === "BIL"
        ? "billed bil"
        : r.positionType === "UBL" || r.positionType === "UNB"
        ? "unbilled ubl unb"
        : (r.positionType || "");

    const searchableString = [
      r.requestCode,
      r.parentRequestCode,
      r.associateName,
      r.associateEmail,
      r.associateLevelCode,
      r.roleTitle,
      r.positionCode,
      positionTypeLabel,
      r.sowName,
      r.accountName,
      r.accountCode,
      r.macName,
      r.currentMacName,
      r.submittedByName,
      r.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return tokens.every((token) => searchableString.includes(token));
  });

  const summary = deriveSummary(allRequests);
  const groups = groupByStatus(filteredRequests);

  const GROUP_CONFIG = [
    { key: "SUBMITTED", title: "Pending PMO Review", color: t.gold, showWhen: ["all", "needs_pmo"] },
    { key: "PMO_APPROVED", title: "At SCG Gate", color: t.accent, showWhen: ["all", "at_scg"] },
    { key: "ACTIVE", title: "Active", color: "#4a9960", showWhen: ["all", "approved"], subFilter: "active" },
    { key: "ENDED", title: "Ended", color: "#7A9E9C", showWhen: ["all", "approved"], subFilter: "ended" },
    { key: "REJECTED", title: "Rejected", color: "#c84040", showWhen: ["all", "rejected"] },
  ];

  const visibleGroups = GROUP_CONFIG.filter((g) => {
    if (!g.showWhen.includes(activeFilter)) return false;
    if (activeFilter === "approved" && approvedSubFilter !== "all") {
      return g.subFilter === approvedSubFilter;
    }
    return true;
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>



      {/* Filter tabs + search */}
      <ApprovalFilters
        summary={summary}
        activeFilter={activeFilter}
        onFilterChange={(f) => { setActiveFilter(f); setSearch(""); setApprovedSubFilter("all"); }}
        search={search}
        onSearchChange={setSearch}
        approvedSubFilter={approvedSubFilter}
        onApprovedSubFilterChange={setApprovedSubFilter}
        activeApprovedCount={groups.ACTIVE.length}
        endedApprovedCount={groups.ENDED.length}
      />

      <PageInfo message="Review and action allocation requests — PMO performs first-level approval, SCG performs second-level sign-off" />

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: "auto", padding: "20px" }}>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: "40px" }}>
            <CircularProgress size={20} sx={{ color: t.accent }} />
          </Box>
        )}

        {!loading && error && (
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px", color: "#c84040",
          }}>
            {error}
          </Typography>
        )}

        {!loading && !error && filteredRequests.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: "60px" }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "12px", color: t.text3, letterSpacing: "0.08em",
            }}>
              No requests found
            </Typography>
          </Box>
        )}

        {!loading && !error && filteredRequests.length > 0 &&
          visibleGroups.map((g) =>
            groups[g.key].length > 0 ? (
              <ApprovalGroup
                key={g.key}
                title={g.title}
                count={groups[g.key].length}
                accentColor={g.color}
                requests={groups[g.key]}
                onReview={(req) => setReviewRequest(req)}
              />
            ) : null
          )
        }
      </Box>

      {/* Review side panel — opens when a card's Review button is clicked */}
      {reviewRequest && (
        <ReviewPanel
          request={reviewRequest}
          onClose={() => setReviewRequest(null)}
          onActionDone={handleActionDone}
        />
      )}
    </Box>
  );
};

export default Approvals;